/**
 * « Repères » : les chiffres clés que les droits permettent de lire, chacun
 * menant à la liste qui l'explique. Section repliée au téléphone (l'accueil
 * dit d'abord quoi faire), ouverte sur ordinateur.
 *
 * Les impayés sont le reste dû des factures échues — paiements et avoirs
 * déduits — calculé par le serveur sur toutes les factures lisibles, jamais
 * la somme des TTC d'une liste tronquée (correctif B9 du 17/10/2026).
 */
import type { ReactNode } from 'react';
import { CalendarDays, EuroIcon, FilePen, TrendingUp } from 'lucide-react';
import { ErrorState } from '../../components/ui/Feedback.js';
import { Section } from '../../components/ui/Section.js';
import { StatGrid, StatTile } from '../../components/ui/StatTile.js';
import { Spinner } from '../../components/ui.js';
import { compteFr, euros } from '../../core/format.js';
import { t } from '../../core/i18n.js';
import { useBreakpoint } from '../../design/tokens.js';
import type { Reperes, TeamToday } from './home-data.js';

export function HomeReperes(props: {
  reperes: { data: Reperes | undefined; isLoading: boolean; error: unknown; refetch: () => unknown } | undefined;
  team: TeamToday | undefined;
}): ReactNode {
  const wide = useBreakpoint('md');
  const data = props.reperes?.data;
  const tiles: ReactNode[] = [];
  if (props.team) {
    const { interventions, active } = props.team.totals;
    tiles.push(
      <StatTile key="interventions" label={t('accueil.interventionsDuJour')} value={interventions}
        hint={active ? `dont ${active} en cours` : undefined} icon={<CalendarDays size={18} />} to="/planning" />,
    );
  }
  if (data?.impayes) {
    tiles.push(
      <StatTile key="impayes" label={t('accueil.impayes')} value={euros(data.impayes.resteDuCents)}
        hint={compteFr(data.impayes.count, 'facture échue', 'factures échues')}
        tone={data.impayes.resteDuCents > 0 ? 'warning' : 'default'} icon={<EuroIcon size={18} />} to="/factures" />,
    );
  }
  if (data?.devisEnAttente) {
    tiles.push(
      <StatTile key="devis" label={t('accueil.devisEnAttente')} value={data.devisEnAttente.count}
        hint={data.devisEnAttente.count ? `${euros(data.devisEnAttente.totalTtcCents)} TTC` : undefined}
        icon={<FilePen size={18} />} to="/devis?etat=envoye" />,
    );
  }
  if (data?.chiffreAffairesMois) {
    tiles.push(
      <StatTile key="ca" label={t('accueil.chiffreAffairesMois')} value={euros(data.chiffreAffairesMois.netHtCents)}
        hint="HT, net des avoirs" icon={<TrendingUp size={18} />} to="/analyses" />,
    );
  }
  const loading = props.reperes?.isLoading && !data;
  const failed = props.reperes?.error && !data;
  if (tiles.length === 0 && !loading && !failed) return null;
  return (
    <Section id="reperes" title={t('accueil.reperes')} collapsible defaultOpen={wide} flush>
      {tiles.length > 0 ? <StatGrid label={t('accueil.reperes')}>{tiles}</StatGrid> : null}
      {loading ? <Spinner variant="section" label="Chargement des repères" /> : null}
      {failed ? <ErrorState error={props.reperes?.error} onRetry={() => void props.reperes?.refetch()} /> : null}
    </Section>
  );
}
